import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { getToken, formatApiErrorDetail } from "@/lib/api";
import { Mic, Loader2, Check, X, Clock, ShieldCheck, MessageSquare, FileText, Mail, Send } from "lucide-react";

const CONF_META = {
  confirmed: { label: "Attorney Confirmed", cls: "bg-emerald-500/15 text-emerald-300", Icon: Check },
  canceled: { label: "Canceled", cls: "bg-red-500/15 text-red-300", Icon: X },
  pending: { label: "Awaiting Confirmation", cls: "bg-amber-500/15 text-amber-300", Icon: Clock },
  none: { label: "Query", cls: "bg-slate-600/30 text-slate-300", Icon: MessageSquare },
};

const STATUS_LABEL = {
  executed: "Executed", recorded: "Recorded for review", responded: "Answered",
  awaiting_confirmation: "Awaiting confirmation", canceled: "Canceled",
  denied: "Denied (unauthorized)", failed: "Failed", unavailable: "Unavailable",
};

// Attorney-facing Crash Ready Voice Audit™ timeline for a single case.
export default function VoiceTimeline() {
  const { caseId } = useParams();
  const [events, setEvents] = useState(undefined);
  const [meta, setMeta] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);

  const load = async () => {
    try {
      const [{ data }, { data: m }] = await Promise.all([
        api.get(`/voice-audit/case/${caseId}`),
        api.get(`/cases/${caseId}/compliance-report/meta`).catch(() => ({ data: null })),
      ]);
      setEvents(data.events || []);
      setMeta(m);
    } catch {
      setEvents([]);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [caseId]);

  const prepareDelivery = async () => {
    try {
      await api.post("/communications", { case_id: caseId, type: "compliance_report" });
      toast.success("Draft created — manage it in Firm → Delivery Center");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  if (events === undefined) {
    return <div className="mt-8 flex justify-center py-8"><Loader2 className="w-5 h-5 text-cyan-400 animate-spin" /></div>;
  }

  const reportUrl = `${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}/compliance-report?auth=${getToken()}`;

  return (
    <div className="mt-8 rounded-2xl border border-slate-800 bg-slate-900/50 p-5" data-testid="voice-timeline">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <Mic className="w-5 h-5 text-cyan-400" />
        <h3 className="font-heading font-bold text-white">Voice Timeline</h3>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-800 text-slate-400">{events.length}</span>
        <a href={reportUrl} target="_blank" rel="noreferrer" data-testid="compliance-report-btn"
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cyan-500 text-slate-950 text-xs font-semibold hover:bg-cyan-400">
          <FileText className="w-3.5 h-3.5" /> Compliance Report
        </a>
        <button onClick={() => setEmailOpen(true)} data-testid="compliance-email-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-800 text-cyan-300 text-xs font-semibold hover:bg-slate-700">
          <Mail className="w-3.5 h-3.5" /> Email
        </button>
        <button onClick={prepareDelivery} data-testid="prepare-delivery-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-800 text-cyan-300 text-xs font-semibold hover:bg-slate-700">
          <Send className="w-3.5 h-3.5" /> Prepare for Delivery
        </button>
        <span className="flex items-center gap-1 text-[10px] text-cyan-300/80"><ShieldCheck className="w-3.5 h-3.5" /> Attorney Approval Model</span>
      </div>
      <p className="text-xs text-slate-500 mb-4">Every Crash Ready Voice™ interaction on this case — immutable and audited. The Compliance Report bundles this trail with the signed retainer and Evidence Quality Certificate™.</p>

      {emailOpen && <ComplianceEmailModal caseId={caseId} meta={meta} onClose={() => setEmailOpen(false)} />}

      {events.length === 0 ? (
        <p className="text-sm text-slate-500">No voice interactions recorded for this case yet.</p>
      ) : (
        <div className="space-y-0">
          {events.map((e, i) => {
            const cs = e.confirmation_status || "none";
            const meta = CONF_META[cs] || CONF_META.none;
            const Icon = meta.Icon;
            return (
              <div key={e.audit_id || i} className="flex gap-3" data-testid={`voice-timeline-event-${i}`}>
                <div className="flex flex-col items-center">
                  <div className="h-8 w-8 rounded-full bg-slate-800 flex items-center justify-center shrink-0 border border-slate-700">
                    <Icon className="w-4 h-4 text-cyan-300" />
                  </div>
                  {i < events.length - 1 && <div className="w-px flex-1 bg-slate-800 my-1" />}
                </div>
                <div className="pb-5 min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${meta.cls}`}>{meta.label}</span>
                    {e.backend_action && <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-300 capitalize">{String(e.backend_action).replace(/_/g, " ")}</span>}
                    <span className="text-[10px] text-slate-500">{STATUS_LABEL[e.status] || e.status}</span>
                    <span className="text-[10px] text-slate-600 uppercase">{e.device || "web"}</span>
                  </div>
                  <div className="text-sm text-slate-200 mt-1"><span className="text-slate-500">“</span>{e.command}<span className="text-slate-500">”</span></div>
                  {(e.result || e.ai_response) && <div className="text-xs text-slate-400 mt-0.5">{e.result || e.ai_response}</div>}
                  <div className="text-[11px] text-slate-500 mt-1">
                    {new Date(e.at).toLocaleString()}
                    {e.attorney_name && <span> · {e.attorney_name}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ComplianceEmailModal({ caseId, meta, onClose }) {
  const enabled = !!meta?.email_enabled;
  const caseNo = meta?.case_number || caseId;
  const [form, setForm] = useState({
    recipient_email: "",
    recipient_name: "",
    reply_to: meta?.default_reply_to || "",
    subject: `Case Compliance Report — ${caseNo}`,
    html_body: `<p>Dear Recipient,</p><p>Please find attached the Case Compliance Report for ${caseNo}, prepared for your review.</p><p>Kind regards,<br/>${meta?.attorney_name || "Attorney"}</p>`,
  });
  const [approved, setApproved] = useState(false);
  const [sending, setSending] = useState(false);
  const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}/compliance-report?auth=${getToken()}`;

  const send = async () => {
    if (!form.recipient_email) { toast.error("Enter a recipient email."); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/cases/${caseId}/compliance-report/email`, { ...form, approved: true });
      toast.success(`Report sent to ${data.recipient}`); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose} data-testid="compliance-email-modal">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-lg w-full p-6 max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-bold text-white flex items-center gap-2"><Mail className="w-5 h-5 text-cyan-400" /> Email Compliance Report</h3>
          <button onClick={onClose} className="p-1.5 rounded-full text-slate-400 hover:bg-slate-800"><X className="w-5 h-5" /></button>
        </div>

        <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-3 mb-4 text-[11px] text-slate-300">
          <ShieldCheck className="w-3.5 h-3.5 text-cyan-300 inline mr-1" />
          Attorney-controlled delivery. The email is sent from your firm with your reply-to address — Crash Ready never sends legal communications on its own. Review every detail before sending.
          {meta?.content_hash && <div className="mt-1.5 font-mono text-[10px] text-slate-500 break-all">SHA-256: {meta.content_hash}</div>}
          {meta?.includes && <div className="mt-1 text-[10px] text-slate-500">Includes: Voice audit ({meta.includes.voice_audit}) · Retainer {meta.includes.signed_retainer ? "✓" : "—"} · Certificate {meta.includes.evidence_certificate ? "✓" : "—"}</div>}
        </div>

        {!enabled && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 mb-4 text-xs text-amber-200">
            Email delivery isn't configured yet (a Resend API key is required). You can still
            <a href={downloadUrl} target="_blank" rel="noreferrer" className="text-cyan-300 underline mx-1">download the full report</a>
            and send it from your own office system.
          </div>
        )}

        <div className="space-y-2 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <input value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })} data-testid="ce-recipient" placeholder="Recipient email" className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white outline-none focus:border-cyan-500" />
            <input value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} data-testid="ce-recipient-name" placeholder="Recipient name (optional)" className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white outline-none focus:border-cyan-500" />
          </div>
          <input value={form.reply_to} onChange={(e) => setForm({ ...form, reply_to: e.target.value })} data-testid="ce-replyto" placeholder="Reply-to (your email)" className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white outline-none focus:border-cyan-500" />
          <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="ce-subject" placeholder="Subject" className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white outline-none focus:border-cyan-500" />
          <textarea value={form.html_body} onChange={(e) => setForm({ ...form, html_body: e.target.value })} rows={6} data-testid="ce-body" className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-xs font-mono outline-none focus:border-cyan-500" />
          <label className="flex items-start gap-2 text-xs text-slate-300 pt-1">
            <input type="checkbox" checked={approved} onChange={(e) => setApproved(e.target.checked)} data-testid="ce-approve" className="mt-0.5" />
            I have reviewed the attached documents and approve sending this Compliance Report to the recipient above.
          </label>
        </div>

        <div className="flex items-center gap-2 mt-4">
          <button onClick={send} disabled={!enabled || !approved || sending} data-testid="ce-send"
            className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-cyan-500 text-slate-950 text-sm font-semibold hover:bg-cyan-400 disabled:opacity-40">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Review & Send
          </button>
          <a href={downloadUrl} target="_blank" rel="noreferrer" data-testid="ce-download" className="px-4 py-2 rounded-full bg-slate-800 text-slate-300 text-sm hover:bg-slate-700">Download instead</a>
        </div>
      </div>
    </div>
  );
}
